import { ReportsToEditor } from "@/components/profile/ReportsToEditor";
import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth, type RoleConfig } from "@/contexts/AuthContext";
import { useFieldPermissions } from "@/hooks/useFieldPermissions";
import { InlineEditableField } from "@/components/InlineEditableField";
import { InlineEditableSection } from "@/components/InlineEditableSection";
import type { EmployeeProfile } from "@/data/employees";
import {
  apiUpdateEmployee,
  apiGetEmployees,
  apiGetDepartments,
  apiGetTeamNames,
  apiGetBankDetails, apiUpdateBankDetails,
  apiGetEmergencyContacts, apiCreateEmergencyContact, apiUpdateEmergencyContact, apiDeleteEmergencyContact,
  apiGetChildren, apiCreateChild, apiUpdateChild, apiDeleteChild,
  apiGetQualifications, apiCreateQualification, apiUpdateQualification, apiDeleteQualification,
  apiGetEquipment, apiCreateEquipment, apiUpdateEquipment, apiDeleteEquipment,
  apiGetVisa, apiUpdateVisa,
  apiGetCompanyDetails,
  apiGetEmploymentDetails, apiUpdateEmploymentDetails,
  apiGetPositions, apiAssignEmployeePosition,
  apiGetCertificateTypes, apiGetTrainingTypes,
  type EquipmentStatus,
  type EmploymentDetails,
  type CertificateType, type TrainingType,
} from "@/lib/api";
import {
  Mail, Phone, MapPin, Calendar, Globe, Briefcase,
  Building2, Users2, Hash, Clock, FileText, Landmark,
  User, CreditCard, Heart, GraduationCap, Monitor, Stamp,
  Plus, Trash2, ShieldCheck, Lock, ClipboardList, AlertTriangle, Baby,
  Pencil, Check, Loader2, X,
} from "lucide-react";
import { COUNTRIES } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  EMPLOYMENT_CONTRACT_TYPES,
  getContractTypeLabel,
  isContractorContractType,
  isFixedTermContractType,
  isFullTimeContractType,
  isPartTimeContractType,
  calculatePartTimeWeeklyHours,
} from "@/lib/employment-contract-types";

interface Props {
  employee: EmployeeProfile;
  isOwnProfile: boolean;
  isLineManagerOfEmployee?: boolean;
  /** When true, replaces the per-section Edit/Save buttons with a single global
   *  "Edit" button and a floating "Save"/"Cancel" bar that edits every section at
   *  once. Used by the Organisation → Employees popup. */
  singleEditMode?: boolean;
}

type SectionKey = "public_profile" | "hr_info" | "employment_details" | "birthday_hire" | "personal_info" | "bank_details" | "emergency_contact" | "children" | "qualifications" | "equipment" | "visa";

const SECTIONS: { key: SectionKey; icon: React.ElementType; labelKey: string }[] = [
  { key: "public_profile", icon: User, labelKey: "profile.sections.publicProfile" },
  { key: "hr_info", icon: Briefcase, labelKey: "profile.sections.hrInfo" },
  { key: "employment_details", icon: ClipboardList, labelKey: "profile.sections.employmentDetails" },
  { key: "birthday_hire", icon: Calendar, labelKey: "profile.sections.birthdayHire" },
  { key: "personal_info", icon: Globe, labelKey: "profile.sections.personalInfo" },
  { key: "bank_details", icon: CreditCard, labelKey: "profile.sections.bankDetails" },
  { key: "emergency_contact", icon: Heart, labelKey: "profile.sections.emergencyContact" },
  { key: "children", icon: Baby, labelKey: "profile.sections.children" },
  { key: "qualifications", icon: GraduationCap, labelKey: "profile.sections.qualifications" },
  { key: "equipment", icon: Monitor, labelKey: "profile.sections.equipment" },
  { key: "visa", icon: Stamp, labelKey: "profile.sections.visa" },
];

// Sections visible to line managers (not HR/admin) when viewing a direct report
const LINE_MANAGER_SECTIONS: SectionKey[] = ["hr_info", "birthday_hire", "qualifications", "equipment", "children"];
function getPartTimeHoursPerDayError(value: number | string | null | undefined, t: (key: string) => string): string {
  if (value === null || value === undefined || value === "") {
    return t("organization.addEmployeeEmployment.partTimeHoursPerDayRequired");
  }
  const hours = Number(value);
  if (!Number.isFinite(hours)) return t("organization.addEmployeeEmployment.partTimeHoursPerDayInvalid");
  if (hours <= 0) return t("organization.addEmployeeEmployment.partTimeHoursPerDayPositive");
  if (hours >= 8) return t("organization.addEmployeeEmployment.partTimeHoursPerDayLessThanFullTime");
  return "";
}

export function ProfilePersonalInfoTab({ employee, isOwnProfile, isLineManagerOfEmployee = false, singleEditMode = false }: Props) {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canEdit, canEditAnyInSection } = useFieldPermissions();

  const viewerRole: RoleConfig = user?.role ?? { mainRole: "user", subRole: "employee" };

  const isViewerHrOrAdmin = viewerRole.mainRole === "hr" || viewerRole.mainRole === "admin";
  // Line managers see only a restricted subset of sections
  const visibleSections = !isViewerHrOrAdmin && isLineManagerOfEmployee && !isOwnProfile
    ? SECTIONS.filter((s) => LINE_MANAGER_SECTIONS.includes(s.key))
    : SECTIONS;
  const showSection = (key: SectionKey) => visibleSections.some((s) => s.key === key);

  const [activeSection, setActiveSection] = useState<SectionKey>(
    !isViewerHrOrAdmin && isLineManagerOfEmployee && !isOwnProfile ? "hr_info" : "public_profile"
  );
  const [editingSection, setEditingSection] = useState<SectionKey | null>(null);
  // Global "edit everything" mode (single-edit-mode only)
  const [editingAll, setEditingAll] = useState(false);
  const sectionEditing = (key: SectionKey) => singleEditMode ? editingAll : editingSection === key;
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // In single-edit mode, hide the per-section Edit/Save controls — one global
  // Edit button + a floating Save/Cancel bar drive all sections at once.
  // Stable wrapper (identity memoised on singleEditMode) so editing inputs don't
  // remount on every keystroke. In single-edit mode it hides per-section controls.
  const Section = useMemo(() => {
    const Wrapped = (p: React.ComponentProps<typeof InlineEditableSection>) => (
      <InlineEditableSection {...p} hideControls={singleEditMode || p.hideControls} />
    );
    return Wrapped;
  }, [singleEditMode]);

  // Line Manager reassignment dialog state
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [newManagerId, setNewManagerId] = useState<string>("");

  // ===== Employee list (for Reports To dropdown) =====
  const { data: allEmployees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: apiGetEmployees,
    staleTime: 5 * 60 * 1000,
  });

  // ===== Departments (for Department dropdown) =====
  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: apiGetDepartments,
    staleTime: 5 * 60 * 1000,
  });
  const departmentOptions = departments.map((d) => ({ value: d.name, label: d.name }));

  // ===== Team names (for Team dropdown) =====
  const { data: teamNames = [] } = useQuery({
    queryKey: ["team-names"],
    queryFn: apiGetTeamNames,
    staleTime: 5 * 60 * 1000,
  });
  const teamOptions = teamNames.map((name) => ({ value: name, label: name }));

  // ===== Positions (for Position dropdown in hr_info) =====
  const { data: positions = [] } = useQuery({
    queryKey: ["positions"],
    queryFn: apiGetPositions,
    staleTime: 5 * 60 * 1000,
  });
  const [positionIdForm, setPositionIdForm] = useState<number | null>(employee.positionId ?? null);

  const { data: companyDetails } = useQuery({
    queryKey: ["company-details"],
    queryFn: apiGetCompanyDetails,
    staleTime: 5 * 60 * 1000,
  });
  const orgCompanyName = companyDetails?.companyName ?? employee.company ?? "";

  const directReports = allEmployees.filter(
    (e) => e.reportsTo?.id === employee.id
  );

  // ===== Employee core form state =====
  const [coreForm, setCoreForm] = useState({
    name: employee.name,
    email: employee.email,
    phone: employee.phone,
    jobTitle: employee.jobTitle,
    department: employee.department,
    team: employee.team,
    location: employee.location,
    company: employee.company,
    employeeId: employee.employeeId,
    status: employee.status,
    contractType: employee.contractType as string,
    workSchedule: employee.workSchedule,
    startDate: employee.startDate,
    birthday: employee.birthday,
    nationality: employee.nationality,
    gender: employee.gender,
    address: employee.address,
    isLineManager: employee.isLineManager,
    fullProfileAccess: employee.fullProfileAccess !== false,
  });
  const selectedContractType = coreForm.contractType || employee.contractType;
  const selectedIsFixedTermContract = isFixedTermContractType(selectedContractType);
  const selectedIsPartTimeContract = isPartTimeContractType(selectedContractType);
  const selectedIsFullTimeContract = isFullTimeContractType(selectedContractType);
  const selectedIsContractorContract = isContractorContractType(selectedContractType);
  const selectedIsLegacyIntern = selectedContractType === "Intern";
  const contractTypeOptions = useMemo(() => {
    const options = EMPLOYMENT_CONTRACT_TYPES.map((option) => ({
      value: option.value,
      label: getContractTypeLabel(option.value, t),
    }));
    if (coreForm.contractType && !options.some((option) => option.value === coreForm.contractType)) {
      return [
        { value: coreForm.contractType, label: getContractTypeLabel(coreForm.contractType, t) },
        ...options,
      ];
    }
    return options;
  }, [coreForm.contractType, t]);

  const handleCoreChange = (field: string, value: string) => {
    setCoreForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleContractTypeChange = (value: string) => {
    setCoreForm((prev) => ({ ...prev, contractType: value }));
    setEmpDetailsForm((prev) => ({
      ...prev,
      contractEndDate: isFixedTermContractType(value) ? prev.contractEndDate : null,
      partTimeHoursPerDay: isPartTimeContractType(value) ? prev.partTimeHoursPerDay : null,
      partTimeIncludesSaturday: isPartTimeContractType(value) ? prev.partTimeIncludesSaturday : false,
      hoursPerWeek: isPartTimeContractType(value)
        ? calculatePartTimeWeeklyHours(prev.partTimeHoursPerDay, prev.partTimeIncludesSaturday)
        : prev.hoursPerWeek,
    }));
    setEmpDetailsErrors({});
  };
  const coreMutation = useMutation({
    mutationFn: async () => {
      // If reassignment is happening, update reports first
      if (!coreForm.isLineManager && showReassignDialog && newManagerId) {
        for (const report of directReports) {
          await apiUpdateEmployee(report.id, {
            ...report,
            reportsToId: newManagerId === "__none__" ? null : newManagerId,
          });
        }
      }

      const result = await apiUpdateEmployee(employee.id, {
        ...coreForm,
        isLineManager: coreForm.isLineManager,
        fullProfileAccess: coreForm.fullProfileAccess,
      });
      if (positionIdForm !== (employee.positionId ?? null)) {
        await apiAssignEmployeePosition(employee.id, positionIdForm);
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["employee"] });
      queryClient.invalidateQueries({ queryKey: ["employee-by-email"] });

      // Keep AuthContext in sync if this is the logged-in user's own profile
      if (isOwnProfile) {
        updateUser({
          name: coreForm.name,
          email: coreForm.email,
          isLineManager: coreForm.isLineManager,
        });
      }

      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingSection(null);
      setShowReassignDialog(false);
      setNewManagerId("");
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  // ===== Bank Details =====
  const { data: bankDetails } = useQuery({
    queryKey: ["bank-details", employee.id],
    queryFn: () => apiGetBankDetails(employee.id),
  });

  const [bankForm, setBankForm] = useState({ bankName: "", iban: "", bicSwift: "", accountHolderName: "" });
  useEffect(() => {
    if (bankDetails) {
      setBankForm({
        bankName: bankDetails.bankName || "",
        iban: bankDetails.iban || "",
        bicSwift: bankDetails.bicSwift || "",
        accountHolderName: bankDetails.accountHolderName || "",
      });
    }
  }, [bankDetails]);

  const bankMutation = useMutation({
    mutationFn: () => apiUpdateBankDetails(employee.id, bankForm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank-details", employee.id] });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingSection(null);
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  // ===== Emergency Contacts =====
  const { data: emergencyContacts = [] } = useQuery({
    queryKey: ["emergency-contacts", employee.id],
    queryFn: () => apiGetEmergencyContacts(employee.id),
  });

  const [ecForms, setEcForms] = useState<{ id?: number; contactName: string; relationship: string; phone: string; email: string }[]>([]);
  useEffect(() => {
    setEcForms(emergencyContacts.map((c) => ({
      id: c.id,
      contactName: c.contactName,
      relationship: c.relationship || "",
      phone: c.phone || "",
      email: c.email || "",
    })));
  }, [emergencyContacts]);

  const ecSaveMutation = useMutation({
    mutationFn: async () => {
      for (const f of ecForms) {
        if (f.id) {
          await apiUpdateEmergencyContact(employee.id, f.id, f);
        } else {
          await apiCreateEmergencyContact(employee.id, f);
        }
      }
      const currentIds = ecForms.filter((f) => f.id).map((f) => f.id);
      for (const c of emergencyContacts) {
        if (!currentIds.includes(c.id)) {
          await apiDeleteEmergencyContact(employee.id, c.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["emergency-contacts", employee.id] });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingSection(null);
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  // ===== Children =====
  const { data: childrenData = [] } = useQuery({
    queryKey: ["employee-children", employee.id],
    queryFn: () => apiGetChildren(employee.id),
  });

  const [childForms, setChildForms] = useState<{ id?: number; firstName: string; dateOfBirth: string }[]>([]);
  useEffect(() => {
    setChildForms(childrenData.map((c) => ({
      id: c.id,
      firstName: c.firstName,
      dateOfBirth: c.dateOfBirth || "",
    })));
  }, [childrenData]);

  const childSaveMutation = useMutation({
    mutationFn: async () => {
      for (const f of childForms) {
        if (f.id) {
          await apiUpdateChild(employee.id, f.id, { firstName: f.firstName, dateOfBirth: f.dateOfBirth });
        } else {
          await apiCreateChild(employee.id, { firstName: f.firstName, dateOfBirth: f.dateOfBirth });
        }
      }
      const currentIds = childForms.filter((f) => f.id).map((f) => f.id);
      for (const c of childrenData) {
        if (!currentIds.includes(c.id)) {
          await apiDeleteChild(employee.id, c.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee-children", employee.id] });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingSection(null);
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  // ===== Qualifications =====
  const { data: qualifications = [] } = useQuery({
    queryKey: ["qualifications", employee.id],
    queryFn: () => apiGetQualifications(employee.id),
  });

  const { data: certTypes = [] } = useQuery<CertificateType[]>({
    queryKey: ["certificate-types"],
    queryFn: apiGetCertificateTypes,
    staleTime: 5 * 60_000,
  });

  const { data: trainingTypes = [] } = useQuery<TrainingType[]>({
    queryKey: ["training-types"],
    queryFn: apiGetTrainingTypes,
    staleTime: 5 * 60_000,
  });

  const [qualForms, setQualForms] = useState<{ id?: number; type: string; title: string; institution: string; dateObtained: string; expiryDate: string; acquireByDate?: string; recurrenceMonths?: number | null }[]>([]);
  useEffect(() => {
    setQualForms(qualifications.map((q: any) => ({
      id: q.id,
      type: q.type,
      title: q.title,
      institution: q.institution || "",
      dateObtained: q.dateObtained || "",
      expiryDate: q.expiryDate || "",
      acquireByDate: q.acquireByDate || "",
      recurrenceMonths: q.recurrenceMonths ?? null,
    })));
  }, [qualifications]);

  const qualSaveMutation = useMutation({
    mutationFn: async () => {
      for (const f of qualForms) {
        if (f.id) {
          await apiUpdateQualification(employee.id, f.id, f as any);
        } else {
          await apiCreateQualification(employee.id, f as any);
        }
      }
      const currentIds = qualForms.filter((f) => f.id).map((f) => f.id);
      for (const q of qualifications) {
        if (!currentIds.includes(q.id)) {
          await apiDeleteQualification(employee.id, q.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qualifications", employee.id] });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingSection(null);
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  // ===== Equipment =====
  const { data: equipmentList = [] } = useQuery({
    queryKey: ["equipment", employee.id],
    queryFn: () => apiGetEquipment(employee.id),
  });

  const [equipForms, setEquipForms] = useState<{
    id?: number;
    itemName: string;
    serialNumber: string;
    externalId: string;
    category: string;
    assignedDate: string;
    returnDate: string;
    expiryDate: string;
    status: EquipmentStatus;
  }[]>([]);
  useEffect(() => {
    setEquipForms(equipmentList.map((e) => ({
      id: e.id,
      itemName: e.itemName,
      serialNumber: e.serialNumber || "",
      externalId: e.externalId || "",
      category: e.category || "",
      assignedDate: e.assignedDate || "",
      returnDate: e.returnDate || "",
      expiryDate: e.expiryDate || "",
      status: e.status,
    })));
  }, [equipmentList]);

  const equipSaveMutation = useMutation({
    mutationFn: async () => {
      for (const f of equipForms) {
        if (f.id) {
          await apiUpdateEquipment(employee.id, f.id, f);
        } else {
          await apiCreateEquipment(employee.id, f as any);
        }
      }
      const currentIds = equipForms.filter((f) => f.id).map((f) => f.id);
      for (const e of equipmentList) {
        if (!currentIds.includes(e.id)) {
          await apiDeleteEquipment(employee.id, e.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["equipment", employee.id] });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingSection(null);
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  // ===== Visa =====
  const { data: visa } = useQuery({
    queryKey: ["visa", employee.id],
    queryFn: () => apiGetVisa(employee.id),
  });

  const [visaForm, setVisaForm] = useState({ permitType: "", permitNumber: "", issuingCountry: "", issueDate: "", expiryDate: "" });
  useEffect(() => {
    if (visa) {
      setVisaForm({
        permitType: visa.permitType || "",
        permitNumber: visa.permitNumber || "",
        issuingCountry: visa.issuingCountry || "",
        issueDate: visa.issueDate || "",
        expiryDate: visa.expiryDate || "",
      });
    }
  }, [visa]);

  const visaMutation = useMutation({
    mutationFn: () => apiUpdateVisa(employee.id, visaForm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["visa", employee.id] });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingSection(null);
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  // ===== Employment Details =====
  const { data: employmentDetails } = useQuery({
    queryKey: ["employment-details", employee.id],
    queryFn: () => apiGetEmploymentDetails(employee.id),
  });

  const defaultEmpDetails: EmploymentDetails = {
    contractEndDate: null, noticePeriodDays: null, workArrangement: null,
    careerLevel: null, costCenter: null, benefitsEligible: false,
    probationEndDate: null, probationPeriodMonths: null,
    hoursPerWeek: null, ftePercentage: null,
    partTimeHoursPerDay: null, partTimeIncludesSaturday: false, overtimeEligible: false,
    contractorCompany: null, scopeOfWork: null, billingCycle: null,
    ndaSigned: false, timeTrackingRequired: false,
    internshipType: null, mentorId: null, schoolUniversity: null,
    academicProgram: null, expectedGraduationDate: null, isConversionEligible: false,
  };

  const [empDetailsForm, setEmpDetailsForm] = useState<EmploymentDetails>(defaultEmpDetails);
  useEffect(() => {
    setEmpDetailsForm({ ...defaultEmpDetails, ...(employmentDetails ?? {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employmentDetails]);

  const [empDetailsErrors, setEmpDetailsErrors] = useState<Record<string, string>>({});

  const partTimeWeeklyHours = selectedIsPartTimeContract
    ? calculatePartTimeWeeklyHours(empDetailsForm.partTimeHoursPerDay, empDetailsForm.partTimeIncludesSaturday)
    : empDetailsForm.hoursPerWeek;

  const buildEmploymentDetailsPayload = (): EmploymentDetails => ({
    ...empDetailsForm,
    contractEndDate: selectedIsFixedTermContract ? empDetailsForm.contractEndDate : null,
    partTimeHoursPerDay: selectedIsPartTimeContract ? Number(empDetailsForm.partTimeHoursPerDay) : null,
    partTimeIncludesSaturday: selectedIsPartTimeContract ? !!empDetailsForm.partTimeIncludesSaturday : false,
    hoursPerWeek: selectedIsPartTimeContract ? partTimeWeeklyHours : empDetailsForm.hoursPerWeek ?? null,
  });

  const validateEmploymentDetailsForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (selectedIsFixedTermContract && !empDetailsForm.contractEndDate) {
      errors.contractEndDate = t("organization.addEmployeeEmployment.fixedTermEndDateMissing");
    }
    if (empDetailsForm.contractEndDate && coreForm.startDate && new Date(empDetailsForm.contractEndDate) <= new Date(coreForm.startDate)) {
      errors.contractEndDate = t("organization.addEmployeeEmployment.endDateAfterStart");
    }
    if (selectedIsPartTimeContract) {
      const partTimeError = getPartTimeHoursPerDayError(empDetailsForm.partTimeHoursPerDay, t);
      if (partTimeError) errors.partTimeHoursPerDay = partTimeError;
    }
    return errors;
  };

  const empDetailsMutation = useMutation({
    mutationFn: () => apiUpdateEmploymentDetails(employee.id, buildEmploymentDetailsPayload()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employment-details", employee.id] });
      setEmpDetailsErrors({});
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingSection(null);
    },
    onError: async (err: any) => {
      try {
        const body = await err?.response?.json?.();
        if (body?.errors) { setEmpDetailsErrors(body.errors); return; }
      } catch { /* ignore */ }
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  // Days-until helper for expiry warnings
  function daysUntil(dateStr: string | null | undefined): number | null {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
    return diff;
  }

  // ===== Helpers =====
  const ce = (field: string) => canEdit(field, viewerRole, isOwnProfile, isLineManagerOfEmployee);

  const handlePositionChange = (posId: string) => {
    if (posId === "__none__") {
      setPositionIdForm(null);
      handleCoreChange("jobTitle", "");
    } else {
      const pos = positions.find((p) => String(p.id) === posId);
      setPositionIdForm(pos?.id ?? null);
      if (pos) handleCoreChange("jobTitle", pos.title);
    }
  };

  const handleSave = (section: SectionKey) => {
    switch (section) {
      case "public_profile":
      case "birthday_hire":
      case "personal_info":
        coreMutation.mutate();
        break;
      case "hr_info":
        if (employee.isLineManager && !coreForm.isLineManager && directReports.length > 0) {
          setShowReassignDialog(true);
        } else {
          coreMutation.mutate();
        }
        break;
      case "bank_details":
        bankMutation.mutate();
        break;
      case "emergency_contact":
        ecSaveMutation.mutate();
        break;
      case "children":
        childSaveMutation.mutate();
        break;
      case "qualifications":
        qualSaveMutation.mutate();
        break;
      case "equipment":
        equipSaveMutation.mutate();
        break;
      case "visa":
        visaMutation.mutate();
        break;
      case "employment_details":
        empDetailsMutation.mutate();
        break;
    }
  };

  const handleCancel = (section: SectionKey) => {
    setEditingSection(null);
    setShowReassignDialog(false);
    if (section === "employment_details") {
      setEmpDetailsForm({ ...defaultEmpDetails, ...(employmentDetails ?? {}) });
      setEmpDetailsErrors({});
      return;
    }
    if (["public_profile", "hr_info", "birthday_hire", "personal_info"].includes(section)) {
      setCoreForm({
        name: employee.name, email: employee.email, phone: employee.phone,
        jobTitle: employee.jobTitle, department: employee.department, team: employee.team,
        location: employee.location, company: employee.company, employeeId: employee.employeeId,
        status: employee.status, contractType: employee.contractType, workSchedule: employee.workSchedule,
        startDate: employee.startDate, birthday: employee.birthday, nationality: employee.nationality,
        gender: employee.gender,
        address: employee.address,
        isLineManager: employee.isLineManager,
        fullProfileAccess: employee.fullProfileAccess !== false,
      });
      setPositionIdForm(employee.positionId ?? null);
    }
  };

  const isSaving = coreMutation.isPending || bankMutation.isPending || ecSaveMutation.isPending || childSaveMutation.isPending || qualSaveMutation.isPending || equipSaveMutation.isPending || visaMutation.isPending || empDetailsMutation.isPending;

  // ===== Global "edit everything at once" (single-edit-mode) =====
  const canEditSomething = visibleSections.some((s) =>
    canEditAnyInSection(s.key, viewerRole, isOwnProfile, isLineManagerOfEmployee)
  );

  const bulkSaveMutation = useMutation({
    mutationFn: async () => {
      // Core employee fields (public_profile / hr_info / birthday_hire / personal_info)
      await apiUpdateEmployee(employee.id, {
        ...coreForm,
        isLineManager: coreForm.isLineManager,
        fullProfileAccess: coreForm.fullProfileAccess,
      });
      if (positionIdForm !== (employee.positionId ?? null)) {
        await apiAssignEmployeePosition(employee.id, positionIdForm);
      }
      if (showSection("employment_details")) {
        await apiUpdateEmploymentDetails(employee.id, buildEmploymentDetailsPayload());
      }
      if (showSection("bank_details")) {
        await apiUpdateBankDetails(employee.id, bankForm);
      }
      if (showSection("emergency_contact")) {
        for (const fEc of ecForms) {
          if (fEc.id) await apiUpdateEmergencyContact(employee.id, fEc.id, fEc);
          else await apiCreateEmergencyContact(employee.id, fEc);
        }
        const ecIds = ecForms.filter((fEc) => fEc.id).map((fEc) => fEc.id);
        for (const c of emergencyContacts) {
          if (!ecIds.includes(c.id)) await apiDeleteEmergencyContact(employee.id, c.id);
        }
      }
      if (showSection("children")) {
        for (const fc of childForms) {
          if (fc.id) await apiUpdateChild(employee.id, fc.id, { firstName: fc.firstName, dateOfBirth: fc.dateOfBirth });
          else await apiCreateChild(employee.id, { firstName: fc.firstName, dateOfBirth: fc.dateOfBirth });
        }
        const childIds = childForms.filter((fc) => fc.id).map((fc) => fc.id);
        for (const c of childrenData) {
          if (!childIds.includes(c.id)) await apiDeleteChild(employee.id, c.id);
        }
      }
      if (showSection("qualifications")) {
        for (const fq of qualForms) {
          if (fq.id) await apiUpdateQualification(employee.id, fq.id, fq as any);
          else await apiCreateQualification(employee.id, fq as any);
        }
        const qIds = qualForms.filter((fq) => fq.id).map((fq) => fq.id);
        for (const q of qualifications) {
          if (!qIds.includes(q.id)) await apiDeleteQualification(employee.id, q.id);
        }
      }
      if (showSection("equipment")) {
        for (const feq of equipForms) {
          if (feq.id) await apiUpdateEquipment(employee.id, feq.id, feq);
          else await apiCreateEquipment(employee.id, feq as any);
        }
        const eqIds = equipForms.filter((feq) => feq.id).map((feq) => feq.id);
        for (const e of equipmentList) {
          if (!eqIds.includes(e.id)) await apiDeleteEquipment(employee.id, e.id);
        }
      }
      if (showSection("visa")) {
        await apiUpdateVisa(employee.id, visaForm);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["employee"] });
      queryClient.invalidateQueries({ queryKey: ["employee-by-email"] });
      queryClient.invalidateQueries({ queryKey: ["bank-details", employee.id] });
      queryClient.invalidateQueries({ queryKey: ["emergency-contacts", employee.id] });
      queryClient.invalidateQueries({ queryKey: ["employee-children", employee.id] });
      queryClient.invalidateQueries({ queryKey: ["qualifications", employee.id] });
      queryClient.invalidateQueries({ queryKey: ["equipment", employee.id] });
      queryClient.invalidateQueries({ queryKey: ["visa", employee.id] });
      queryClient.invalidateQueries({ queryKey: ["employment-details", employee.id] });
      if (isOwnProfile) {
        updateUser({ name: coreForm.name, email: coreForm.email, isLineManager: coreForm.isLineManager });
      }
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
      setEditingAll(false);
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  const handleGlobalSave = () => {
    const errors = validateEmploymentDetailsForm();
    if (Object.keys(errors).length > 0) {
      setEmpDetailsErrors(errors);
      scrollToSection("employment_details");
      return;
    }
    bulkSaveMutation.mutate();
  };
  const handleGlobalCancel = () => {
    setEditingAll(false);
    // Reset every section's form back to server state
    setCoreForm({
      name: employee.name, email: employee.email, phone: employee.phone,
      jobTitle: employee.jobTitle, department: employee.department, team: employee.team,
      location: employee.location, company: employee.company, employeeId: employee.employeeId,
      status: employee.status, contractType: employee.contractType, workSchedule: employee.workSchedule,
      startDate: employee.startDate, birthday: employee.birthday, nationality: employee.nationality,
      gender: employee.gender, address: employee.address,
      isLineManager: employee.isLineManager, fullProfileAccess: employee.fullProfileAccess !== false,
    });
    setPositionIdForm(employee.positionId ?? null);
    setEmpDetailsForm({ ...defaultEmpDetails, ...(employmentDetails ?? {}) });
    if (bankDetails) setBankForm({ bankName: bankDetails.bankName || "", iban: bankDetails.iban || "", bicSwift: bankDetails.bicSwift || "", accountHolderName: bankDetails.accountHolderName || "" });
    setEcForms(emergencyContacts.map((c) => ({ id: c.id, contactName: c.contactName, relationship: c.relationship || "", phone: c.phone || "", email: c.email || "" })));
    setChildForms(childrenData.map((c) => ({ id: c.id, firstName: c.firstName, dateOfBirth: c.dateOfBirth || "" })));
    setQualForms(qualifications.map((q: any) => ({ id: q.id, type: q.type, title: q.title, institution: q.institution || "", dateObtained: q.dateObtained || "", expiryDate: q.expiryDate || "", acquireByDate: q.acquireByDate || "", recurrenceMonths: q.recurrenceMonths ?? null })));
    setEquipForms(equipmentList.map((e) => ({ id: e.id, itemName: e.itemName, serialNumber: e.serialNumber || "", externalId: e.externalId || "", category: e.category || "", assignedDate: e.assignedDate || "", returnDate: e.returnDate || "", expiryDate: e.expiryDate || "", status: e.status })));
    if (visa) setVisaForm({ permitType: visa.permitType || "", permitNumber: visa.permitNumber || "", issuingCountry: visa.issuingCountry || "", issueDate: visa.issueDate || "", expiryDate: visa.expiryDate || "" });
  };

  const scrollToSection = (key: SectionKey) => {
    setActiveSection(key);
    sectionRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className={singleEditMode ? "relative" : undefined}>
      {/* Global edit toggle (single-edit mode) */}
      {singleEditMode && canEditSomething && !editingAll && (
        <div className="flex justify-end mb-3">
          <Button size="sm" onClick={() => setEditingAll(true)} className="gap-1.5">
            <Pencil className="h-3.5 w-3.5" />{t("common.edit")}
          </Button>
        </div>
      )}
      <div className="flex gap-6">
      {/* Sidebar */}
      <div className="w-56 shrink-0 hidden lg:block">
        <div className="sticky top-4">
          <ScrollArea className="h-fit">
            <nav className="space-y-0.5">
              {visibleSections.map((s) => {
                const canEditSection = canEditAnyInSection(s.key, viewerRole, isOwnProfile, isLineManagerOfEmployee);
                return (
                  <button
                    key={s.key}
                    onClick={() => scrollToSection(s.key)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors text-left",
                      activeSection === s.key
                        ? "bg-accent text-accent-foreground font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}
                  >
                    <s.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate flex-1">{t(s.labelKey)}</span>
                    {!canEditSection && (
                      <span className="text-[10px] text-muted-foreground/60 shrink-0">🔒</span>
                    )}
                  </button>
                );
              })}
            </nav>
          </ScrollArea>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-4">
        {/* Public Profile */}
        {showSection("public_profile") && (
          <div ref={(el) => { sectionRefs.current.public_profile = el; }} className="space-y-4">
            {/* ── Organisation Info — always read-only for employees ──────── */}
            {!isViewerHrOrAdmin && (
              <Card className="shadow-card">
                <CardHeader className="pb-3 pt-4 px-4">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-3.5 w-3.5" />
                    {t("profile.sections.organisationInfo")}
                    <span className="ml-auto flex items-center gap-1 text-[10px] font-normal text-muted-foreground/70 bg-muted/60 px-2 py-0.5 rounded-full border border-border/50">
                      <Lock className="h-2.5 w-2.5" />
                      {t("profile.managedByHR")}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-0 divide-y divide-border">
                  {[
                    { icon: Briefcase,  label: t("profile.jobTitleLabel"),   value: employee.jobTitle   },
                    { icon: Building2,  label: t("profile.departmentLabel"), value: employee.department },
                    { icon: Users2,     label: t("profile.teamLabel"),       value: employee.team       },
                    { icon: MapPin,     label: t("profile.locationLabel"),   value: employee.location   },
                    { icon: Landmark,   label: t("profile.companyLabel"),    value: orgCompanyName      },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-start gap-3 py-2.5">
                      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="text-sm font-medium text-foreground mt-0.5">
                          {value || <span className="text-muted-foreground italic">{t("common.notSet")}</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* ── Contact Info — editable fields only ─────────────────────── */}
            <Section
              title={isViewerHrOrAdmin ? t("profile.sections.publicProfile") : t("profile.sections.contactInfo")}
              icon={User}
              sectionKey="public_profile"
              canEditAny={canEditAnyInSection("public_profile", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
              isEditing={sectionEditing("public_profile")}
              onEdit={() => setEditingSection("public_profile")}
              onSave={() => handleSave("public_profile")}
              onCancel={() => handleCancel("public_profile")}
              isSaving={coreMutation.isPending}
            >
              <InlineEditableField label={t("profile.nameLabel")} value={coreForm.name} fieldName="name" isEditing={sectionEditing("public_profile")} canEdit={ce("name")} onChange={handleCoreChange} icon={User} />
              <InlineEditableField label={t("profile.emailLabel")} value={coreForm.email} fieldName="email" isEditing={sectionEditing("public_profile")} canEdit={ce("email")} onChange={handleCoreChange} icon={Mail} />
              <InlineEditableField label={t("profile.phoneLabel")} value={coreForm.phone} fieldName="phone" isEditing={sectionEditing("public_profile")} canEdit={ce("phone")} onChange={handleCoreChange} icon={Phone} />
              {/* Org fields — only shown here for HR/Admin who can edit them */}
              {isViewerHrOrAdmin && (
                <>
                  <InlineEditableField
                    label={t("profile.positionLabel")}
                    value={sectionEditing("public_profile")
                      ? (positionIdForm !== null ? String(positionIdForm) : "__none__")
                      : (employee.jobTitle || "")}
                    fieldName="job_title"
                    type={sectionEditing("public_profile") ? "select" : "text"}
                    options={[
                      { value: "__none__", label: `— ${t("common.notSet")} —` },
                      ...positions.map((p) => ({ value: String(p.id), label: p.title })),
                    ]}
                    isEditing={sectionEditing("public_profile")}
                    canEdit={ce("job_title")}
                    onChange={(_, v) => handlePositionChange(v)}
                    icon={Briefcase}
                  />
                  <InlineEditableField label={t("profile.departmentLabel")} value={coreForm.department} fieldName="department" type="select" options={departmentOptions} isEditing={sectionEditing("public_profile")} canEdit={ce("department")} onChange={handleCoreChange} icon={Building2} />
                  <InlineEditableField label={t("profile.teamLabel")} value={coreForm.team} fieldName="team" type="select" options={teamOptions} isEditing={sectionEditing("public_profile")} canEdit={ce("team")} onChange={handleCoreChange} icon={Users2} />
                  <InlineEditableField label={t("profile.locationLabel")} value={coreForm.location} fieldName="location" type="select" options={COUNTRIES} isEditing={sectionEditing("public_profile")} canEdit={ce("location")} onChange={handleCoreChange} icon={MapPin} />
                  <InlineEditableField label={t("profile.companyLabel")} value={orgCompanyName} fieldName="company" isEditing={sectionEditing("public_profile")} canEdit={false} onChange={handleCoreChange} icon={Landmark} />
                </>
              )}
            </Section>
          </div>
        )}

        {/* HR Information */}
        <div ref={(el) => { sectionRefs.current.hr_info = el; }}>
          <Section
            title={t("profile.sections.hrInfo")}
            icon={Briefcase}
            sectionKey="hr_info"
            canEditAny={canEditAnyInSection("hr_info", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
            isEditing={sectionEditing("hr_info")}
            onEdit={() => setEditingSection("hr_info")}
            onSave={() => handleSave("hr_info")}
            onCancel={() => handleCancel("hr_info")}
            isSaving={coreMutation.isPending}
          >
            <InlineEditableField label={t("profile.employeeIdLabel")} value={coreForm.employeeId} fieldName="employee_id" isEditing={sectionEditing("hr_info")} canEdit={false} onChange={(_, v) => handleCoreChange("employeeId", v)} icon={Hash} />
            <InlineEditableField label={t("profile.statusLabel")} value={coreForm.status} fieldName="status" type="select" options={[{ value: "Active", label: t("common.active") }, { value: "Disabled", label: t("common.disabled") }]} isEditing={sectionEditing("hr_info")} canEdit={ce("status")} onChange={(_, v) => handleCoreChange("status", v)} icon={FileText} />
            <InlineEditableField
              label={t("profile.contractTypeLabel")}
              value={sectionEditing("hr_info") && ce("contract_type") ? coreForm.contractType : getContractTypeLabel(coreForm.contractType, t)}
              fieldName="contract_type"
              type={sectionEditing("hr_info") && ce("contract_type") ? "select" : "text"}
              options={contractTypeOptions}
              isEditing={sectionEditing("hr_info")}
              canEdit={ce("contract_type")}
              onChange={(_, v) => handleContractTypeChange(v)}
              icon={FileText}
            />
            <InlineEditableField label={t("profile.workScheduleLabel")} value={coreForm.workSchedule} fieldName="work_schedule" isEditing={sectionEditing("hr_info")} canEdit={ce("work_schedule")} onChange={(_, v) => handleCoreChange("workSchedule", v)} icon={Clock} />
            <InlineEditableField label={t("profile.startDateLabel")} value={coreForm.startDate} fieldName="start_date" type="date" isEditing={sectionEditing("hr_info")} canEdit={ce("start_date")} onChange={(_, v) => handleCoreChange("startDate", v)} icon={Calendar} />
            <InlineEditableField
              label={t("profile.isLineManagerLabel")}
              value={coreForm.isLineManager}
              fieldName="is_line_manager"
              type="toggle"
              isEditing={sectionEditing("hr_info")}
              canEdit={ce("is_line_manager")}
              onChange={(_, v) => setCoreForm((prev) => ({ ...prev, isLineManager: v === "true" }))}
              icon={ShieldCheck}
            />
            {/* Full Profile Access toggle — only for line managers, only HR/Admin can toggle */}
            {coreForm.isLineManager && (viewerRole.mainRole === "hr" || viewerRole.mainRole === "admin") && (
              <InlineEditableField
                label={t("profile.fullProfileAccessLabel")}
                value={coreForm.fullProfileAccess}
                fieldName="full_profile_access"
                type="toggle"
                isEditing={sectionEditing("hr_info")}
                canEdit={viewerRole.mainRole === "hr" || viewerRole.mainRole === "admin"}
                onChange={(_, v) => setCoreForm((prev) => ({ ...prev, fullProfileAccess: v === "true" }))}
                icon={ShieldCheck}
              />
            )}
          </Section>
        </div>

        {/* Birthday & Hiring Date */}
        <div ref={(el) => { sectionRefs.current.birthday_hire = el; }}>
          <Section
            title={t("profile.sections.birthdayHire")}
            icon={Calendar}
            sectionKey="birthday_hire"
            canEditAny={canEditAnyInSection("birthday_hire", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
            isEditing={sectionEditing("birthday_hire")}
            onEdit={() => setEditingSection("birthday_hire")}
            onSave={() => handleSave("birthday_hire")}
            onCancel={() => handleCancel("birthday_hire")}
            isSaving={coreMutation.isPending}
          >
            <InlineEditableField label={t("profile.birthdayLabel")} value={coreForm.birthday} fieldName="birthday" type="date" isEditing={sectionEditing("birthday_hire")} canEdit={ce("birthday")} onChange={handleCoreChange} icon={Calendar} />
            <InlineEditableField label={t("profile.startDateLabel")} value={coreForm.startDate} fieldName="start_date" type="date" isEditing={sectionEditing("birthday_hire")} canEdit={ce("start_date")} onChange={(_, v) => handleCoreChange("startDate", v)} icon={Calendar} />
          </Section>
        </div>

        {/* Personal Information */}
        {showSection("personal_info") && (
          <div ref={(el) => { sectionRefs.current.personal_info = el; }}>
            <Section
              title={t("profile.sections.personalInfo")}
              icon={Globe}
              sectionKey="personal_info"
              canEditAny={canEditAnyInSection("personal_info", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
              isEditing={sectionEditing("personal_info")}
              onEdit={() => setEditingSection("personal_info")}
              onSave={() => handleSave("personal_info")}
              onCancel={() => handleCancel("personal_info")}
              isSaving={coreMutation.isPending}
            >
              <InlineEditableField label={t("profile.nationalityLabel")} value={coreForm.nationality} fieldName="nationality" type="select" options={COUNTRIES} isEditing={sectionEditing("personal_info")} canEdit={ce("nationality")} onChange={handleCoreChange} icon={Globe} />
              <InlineEditableField label={t("profile.genderLabel")} value={coreForm.gender} fieldName="gender" type="select" options={[{ value: "male", label: t("profile.genderOptions.male") }, { value: "female", label: t("profile.genderOptions.female") }, { value: "other", label: t("profile.genderOptions.other") }, { value: "prefer_not_to_say", label: t("profile.genderOptions.preferNotToSay") }]} isEditing={sectionEditing("personal_info")} canEdit={ce("gender")} onChange={handleCoreChange} icon={User} />
              <InlineEditableField label={t("profile.addressLabel")} value={coreForm.address} fieldName="address" isEditing={sectionEditing("personal_info")} canEdit={ce("address")} onChange={handleCoreChange} icon={MapPin} />
            </Section>
          </div>
        )}

        {/* Bank Details */}
        {showSection("bank_details") && (
          <div ref={(el) => { sectionRefs.current.bank_details = el; }}>
            <Section
              title={t("profile.sections.bankDetails")}
              icon={CreditCard}
              sectionKey="bank_details"
              canEditAny={canEditAnyInSection("bank_details", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
              isEditing={sectionEditing("bank_details")}
              onEdit={() => setEditingSection("bank_details")}
              onSave={() => handleSave("bank_details")}
              onCancel={() => { setEditingSection(null); if (bankDetails) setBankForm({ bankName: bankDetails.bankName || "", iban: bankDetails.iban || "", bicSwift: bankDetails.bicSwift || "", accountHolderName: bankDetails.accountHolderName || "" }); }}
              isSaving={bankMutation.isPending}
            >
              <InlineEditableField label={t("profile.bank.bankName")} value={bankForm.bankName} fieldName="bank_name" isEditing={sectionEditing("bank_details")} canEdit={ce("bank_name")} onChange={(_, v) => setBankForm((p) => ({ ...p, bankName: v }))} icon={CreditCard} />
              <InlineEditableField label={t("profile.bank.iban")} value={bankForm.iban} fieldName="iban" isEditing={sectionEditing("bank_details")} canEdit={ce("iban")} onChange={(_, v) => setBankForm((p) => ({ ...p, iban: v }))} icon={Hash} />
              <InlineEditableField label={t("profile.bank.bicSwift")} value={bankForm.bicSwift} fieldName="bic_swift" isEditing={sectionEditing("bank_details")} canEdit={ce("bic_swift")} onChange={(_, v) => setBankForm((p) => ({ ...p, bicSwift: v }))} icon={Hash} />
              <InlineEditableField label={t("profile.bank.accountHolder")} value={bankForm.accountHolderName} fieldName="account_holder_name" isEditing={sectionEditing("bank_details")} canEdit={ce("account_holder_name")} onChange={(_, v) => setBankForm((p) => ({ ...p, accountHolderName: v }))} icon={User} />
            </Section>
          </div>
        )}

        {/* Emergency Contacts */}
        {showSection("emergency_contact") && <div ref={(el) => { sectionRefs.current.emergency_contact = el; }}>
          <Section
            title={t("profile.sections.emergencyContact")}
            icon={Heart}
            sectionKey="emergency_contact"
            canEditAny={canEditAnyInSection("emergency_contact", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
            isEditing={sectionEditing("emergency_contact")}
            onEdit={() => setEditingSection("emergency_contact")}
            onSave={() => handleSave("emergency_contact")}
            onCancel={() => { setEditingSection(null); setEcForms(emergencyContacts.map((c) => ({ id: c.id, contactName: c.contactName, relationship: c.relationship || "", phone: c.phone || "", email: c.email || "" }))); }}
            isSaving={ecSaveMutation.isPending}
          >
            {ecForms.length === 0 && !sectionEditing("emergency_contact") && (
              <p className="text-sm text-muted-foreground py-3 italic">{t("common.notSet")}</p>
            )}
            {ecForms.map((ec, i) => (
              <div key={ec.id ?? `new-${i}`} className="py-2">
                {sectionEditing("emergency_contact") && (
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-muted-foreground">#{i + 1}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => setEcForms((prev) => prev.filter((_, idx) => idx !== i))}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <InlineEditableField label={t("profile.emergency.contactName")} value={ec.contactName} fieldName="emergency_contact_name" isEditing={sectionEditing("emergency_contact")} canEdit={ce("emergency_contact_name")} onChange={(_, v) => setEcForms((prev) => prev.map((item, idx) => idx === i ? { ...item, contactName: v } : item))} icon={User} />
                <InlineEditableField label={t("profile.emergency.relationship")} value={ec.relationship} fieldName="emergency_relationship" isEditing={sectionEditing("emergency_contact")} canEdit={ce("emergency_relationship")} onChange={(_, v) => setEcForms((prev) => prev.map((item, idx) => idx === i ? { ...item, relationship: v } : item))} icon={Heart} />
                <InlineEditableField label={t("profile.emergency.phone")} value={ec.phone} fieldName="emergency_phone" isEditing={sectionEditing("emergency_contact")} canEdit={ce("emergency_phone")} onChange={(_, v) => setEcForms((prev) => prev.map((item, idx) => idx === i ? { ...item, phone: v } : item))} icon={Phone} />
                <InlineEditableField label={t("profile.emergency.email")} value={ec.email} fieldName="emergency_email" isEditing={sectionEditing("emergency_contact")} canEdit={ce("emergency_email")} onChange={(_, v) => setEcForms((prev) => prev.map((item, idx) => idx === i ? { ...item, email: v } : item))} icon={Mail} />
              </div>
            ))}
            {sectionEditing("emergency_contact") && ce("emergency_contact_name") && (
              <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => setEcForms((prev) => [...prev, { contactName: "", relationship: "", phone: "", email: "" }])}>
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("common.add")}
              </Button>
            )}
          </Section>
        </div>}

        {/* Children */}
        {showSection("children") && <div ref={(el) => { sectionRefs.current.children = el; }}>
          <Section
            title={t("profile.sections.children")}
            icon={Baby}
            sectionKey="children"
            canEditAny={canEditAnyInSection("children", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
            isEditing={sectionEditing("children")}
            onEdit={() => setEditingSection("children")}
            onSave={() => handleSave("children")}
            onCancel={() => { setEditingSection(null); setChildForms(childrenData.map((c) => ({ id: c.id, firstName: c.firstName, dateOfBirth: c.dateOfBirth || "" }))); }}
            isSaving={childSaveMutation.isPending}
          >
            {childForms.length === 0 && !sectionEditing("children") && (
              <p className="text-sm text-muted-foreground py-3 italic">{t("common.notSet")}</p>
            )}
            {childForms.map((child, i) => {
              const age = child.dateOfBirth
                ? (() => {
                    const dob = new Date(child.dateOfBirth);
                    const today = new Date();
                    let a = today.getFullYear() - dob.getFullYear();
                    const m = today.getMonth() - dob.getMonth();
                    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) a--;
                    return a;
                  })()
                : null;
              return (
                <div key={child.id ?? `new-${i}`} className="py-2">
                  {sectionEditing("children") && (
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-medium text-muted-foreground">#{i + 1}</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => setChildForms((prev) => prev.filter((_, idx) => idx !== i))}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                  <InlineEditableField
                    label={t("profile.children.firstName")}
                    value={child.firstName}
                    fieldName="child_first_name"
                    isEditing={sectionEditing("children")}
                    canEdit={ce("child_first_name")}
                    onChange={(_, v) => setChildForms((prev) => prev.map((item, idx) => idx === i ? { ...item, firstName: v } : item))}
                    icon={User}
                  />
                  {sectionEditing("children") ? (
                    <InlineEditableField
                      label={t("profile.children.dateOfBirth")}
                      value={child.dateOfBirth}
                      fieldName="child_date_of_birth"
                      type="date"
                      isEditing
                      canEdit={ce("child_date_of_birth")}
                      onChange={(_, v) => setChildForms((prev) => prev.map((item, idx) => idx === i ? { ...item, dateOfBirth: v } : item))}
                      icon={Calendar}
                    />
                  ) : (
                    <div className="flex items-start gap-3 py-2">
                      <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-muted-foreground">{t("profile.children.dateOfBirth")}</p>
                        <p className="text-sm font-medium text-foreground mt-0.5">
                          {child.dateOfBirth
                            ? `${child.dateOfBirth}${age != null ? ` (${age} ${age === 1 ? t("profile.children.yearOld") : t("profile.children.yearsOld")})` : ""}`
                            : <span className="text-muted-foreground italic">{t("common.notSet")}</span>}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {sectionEditing("children") && ce("child_first_name") && (
              <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => setChildForms((prev) => [...prev, { firstName: "", dateOfBirth: "" }])}>
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("common.add")}
              </Button>
            )}
          </Section>
        </div>}

        {/* Qualifications */}
        <div ref={(el) => { sectionRefs.current.qualifications = el; }}>
          <Section
            title={t("profile.sections.qualifications")}
            icon={GraduationCap}
            sectionKey="qualifications"
            canEditAny={canEditAnyInSection("qualifications", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
            isEditing={sectionEditing("qualifications")}
            onEdit={() => setEditingSection("qualifications")}
            onSave={() => handleSave("qualifications")}
            onCancel={() => { setEditingSection(null); setQualForms(qualifications.map((q: any) => ({ id: q.id, type: q.type, title: q.title, institution: q.institution || "", dateObtained: q.dateObtained || "", expiryDate: q.expiryDate || "", acquireByDate: q.acquireByDate || "", recurrenceMonths: q.recurrenceMonths ?? null }))); }}
            isSaving={qualSaveMutation.isPending}
          >
            {qualForms.length === 0 && !sectionEditing("qualifications") && (
              <p className="text-sm text-muted-foreground py-3 italic">{t("common.notSet")}</p>
            )}
            {qualForms.map((q, i) => (
              <div key={q.id ?? `new-${i}`} className="py-2">
                {sectionEditing("qualifications") && (
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-muted-foreground">#{i + 1}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => setQualForms((prev) => prev.filter((_, idx) => idx !== i))}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <InlineEditableField label={t("profile.qualification.type")} value={q.type} fieldName="qualification_title" type="select" options={[{ value: "education", label: t("profile.qualification.education") }, { value: "certification", label: t("profile.qualification.certification") }, { value: "training", label: t("profile.qualification.training", "Training") }]} isEditing={sectionEditing("qualifications")} canEdit={ce("qualification_title")} onChange={(_, v) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, type: v } : item))} icon={GraduationCap} />
                {sectionEditing("qualifications") && ce("qualification_title") && q.type === "certification" ? (
                  <div className="flex items-center gap-3 py-1.5 min-h-[2.25rem]">
                    <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <span className="text-[11px] text-muted-foreground">{t("profile.qualification.title")}</span>
                      {certTypes.length > 0 && !certTypes.some((ct) => ct.name === q.title) && q.title ? (
                        <div className="flex gap-1.5">
                          <input
                            value={q.title}
                            onChange={(e) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, title: e.target.value } : item))}
                            placeholder={t("certifications.certTypes.namePlaceholder", "Certificate name...")}
                            className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                          />
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground underline whitespace-nowrap"
                            onClick={() => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, title: "" } : item))}
                          >{t("certifications.certTypes.pickFromList", "Pick from list")}</button>
                        </div>
                      ) : (
                        <select
                          value={q.title}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, title: val === "__custom__" ? "" : val } : item));
                          }}
                          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                        >
                          <option value="">{t("certifications.certTypes.selectCert", "Select certificate...")}</option>
                          {certTypes.map((ct) => (
                            <option key={ct.id} value={ct.name}>{ct.name}</option>
                          ))}
                          <option value="__custom__">+ {t("certifications.certTypes.addCustom", "Custom name...")}</option>
                        </select>
                      )}
                    </div>
                  </div>
                ) : sectionEditing("qualifications") && ce("qualification_title") && q.type === "training" ? (
                  <div className="flex items-center gap-3 py-1.5 min-h-[2.25rem]">
                    <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <span className="text-[11px] text-muted-foreground">{t("profile.qualification.title")}</span>
                      {trainingTypes.length > 0 && !trainingTypes.some((tt) => tt.name === q.title) && q.title ? (
                        <div className="flex gap-1.5">
                          <input
                            value={q.title}
                            onChange={(e) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, title: e.target.value } : item))}
                            placeholder={t("certifications.trainings.trainingTypes.namePlaceholder", "Training name...")}
                            className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                          />
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground underline whitespace-nowrap"
                            onClick={() => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, title: "" } : item))}
                          >{t("certifications.certTypes.pickFromList", "Pick from list")}</button>
                        </div>
                      ) : (
                        <select
                          value={q.title}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "__custom__") {
                              setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, title: "" } : item));
                            } else {
                              const tt = trainingTypes.find((t) => t.name === val);
                              setQualForms((prev) => prev.map((item, idx) => idx === i ? {
                                ...item,
                                title: val,
                                recurrenceMonths: tt?.isRecurring ? (tt.recurrenceMonths ?? null) : null,
                              } : item));
                            }
                          }}
                          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                        >
                          <option value="">{t("certifications.trainings.selectTraining", "Select training...")}</option>
                          {trainingTypes.map((tt) => (
                            <option key={tt.id} value={tt.name}>{tt.name}{tt.isRecurring && tt.recurrenceMonths ? ` (every ${tt.recurrenceMonths}mo)` : ""}</option>
                          ))}
                          <option value="__custom__">+ {t("certifications.trainings.addCustom", "Custom name...")}</option>
                        </select>
                      )}
                    </div>
                  </div>
                ) : (
                  <InlineEditableField label={t("profile.qualification.title")} value={q.title} fieldName="qualification_title" isEditing={sectionEditing("qualifications")} canEdit={ce("qualification_title")} onChange={(_, v) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, title: v } : item))} icon={FileText} />
                )}
                <InlineEditableField label={t("profile.qualification.institution")} value={q.institution} fieldName="qualification_institution" isEditing={sectionEditing("qualifications")} canEdit={ce("qualification_institution")} onChange={(_, v) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, institution: v } : item))} icon={Building2} />
                <InlineEditableField label={q.type === "training" ? t("certifications.trainings.completionDate", "Completion date") : t("profile.qualification.dateObtained")} value={q.dateObtained} fieldName="qualification_date" type="date" isEditing={sectionEditing("qualifications")} canEdit={ce("qualification_date")} onChange={(_, v) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, dateObtained: v } : item))} icon={Calendar} />
                <InlineEditableField label={t("profile.qualification.expiryDate")} value={q.expiryDate} fieldName="qualification_date" type="date" isEditing={sectionEditing("qualifications")} canEdit={ce("qualification_date")} onChange={(_, v) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, expiryDate: v } : item))} icon={Calendar} />
                {q.type === "certification" && (
                  <InlineEditableField label={t("certifications.acquireByDate", "Acquire by")} value={q.acquireByDate ?? ""} fieldName="qualification_date" type="date" isEditing={sectionEditing("qualifications")} canEdit={ce("qualification_date")} onChange={(_, v) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, acquireByDate: v } : item))} icon={Calendar} />
                )}
                {q.type === "training" && sectionEditing("qualifications") && ce("qualification_date") && (
                  <div className="flex items-center gap-3 py-1.5 min-h-[2.25rem]">
                    <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                      <span className="text-[11px] text-muted-foreground">{t("certifications.trainings.trainingTypes.recurrenceMonths", "Recurrence (months)")}</span>
                      <input
                        type="number"
                        min="1"
                        value={q.recurrenceMonths ?? ""}
                        onChange={(e) => setQualForms((prev) => prev.map((item, idx) => idx === i ? { ...item, recurrenceMonths: e.target.value ? Number(e.target.value) : null } : item))}
                        placeholder={t("certifications.trainings.trainingTypes.recurrencePlaceholder", "e.g. 12 (leave blank for one-time)")}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      />
                    </div>
                  </div>
                )}
                {q.type === "training" && !sectionEditing("qualifications") && q.recurrenceMonths && (
                  <InlineEditableField label={t("certifications.trainings.trainingTypes.recurrenceMonths", "Recurrence (months)")} value={String(q.recurrenceMonths)} fieldName="qualification_date" isEditing={false} canEdit={false} onChange={() => {}} icon={Calendar} />
                )}
              </div>
            ))}
            {sectionEditing("qualifications") && ce("qualification_title") && (
              <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => setQualForms((prev) => [...prev, { type: "education", title: "", institution: "", dateObtained: "", expiryDate: "" }])}>
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("common.add")}
              </Button>
            )}
          </Section>
        </div>

        {/* Equipment */}
        <div ref={(el) => { sectionRefs.current.equipment = el; }}>
          <Section
            title={t("profile.sections.equipment")}
            icon={Monitor}
            sectionKey="equipment"
            canEditAny={canEditAnyInSection("equipment", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
            isEditing={sectionEditing("equipment")}
            onEdit={() => setEditingSection("equipment")}
            onSave={() => handleSave("equipment")}
            onCancel={() => { setEditingSection(null); setEquipForms(equipmentList.map((e) => ({ id: e.id, itemName: e.itemName, serialNumber: e.serialNumber || "", externalId: e.externalId || "", category: e.category || "", assignedDate: e.assignedDate || "", returnDate: e.returnDate || "", expiryDate: e.expiryDate || "", status: e.status }))); }}
            isSaving={equipSaveMutation.isPending}
          >
            {equipForms.length === 0 && !sectionEditing("equipment") && (
              <p className="text-sm text-muted-foreground py-3 italic">{t("common.notSet")}</p>
            )}
            {equipForms.map((eq, i) => (
              <div key={eq.id ?? `new-${i}`} className="py-2">
                {sectionEditing("equipment") && (
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-muted-foreground">#{i + 1}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => setEquipForms((prev) => prev.filter((_, idx) => idx !== i))}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <InlineEditableField label={t("profile.equipmentFields.itemName")} value={eq.itemName} fieldName="equipment_item" isEditing={sectionEditing("equipment")} canEdit={ce("equipment_item")} onChange={(_, v) => setEquipForms((prev) => prev.map((item, idx) => idx === i ? { ...item, itemName: v } : item))} icon={Monitor} />
                <InlineEditableField label={t("profile.equipmentFields.serialNumber")} value={eq.serialNumber} fieldName="equipment_serial" isEditing={sectionEditing("equipment")} canEdit={ce("equipment_serial")} onChange={(_, v) => setEquipForms((prev) => prev.map((item, idx) => idx === i ? { ...item, serialNumber: v } : item))} icon={Hash} />
                <InlineEditableField label={t("profile.equipmentFields.externalId")} value={eq.externalId} fieldName="equipment_serial" isEditing={sectionEditing("equipment")} canEdit={ce("equipment_serial")} onChange={(_, v) => setEquipForms((prev) => prev.map((item, idx) => idx === i ? { ...item, externalId: v } : item))} icon={Hash} />
                <InlineEditableField label={t("profile.equipmentFields.category")} value={eq.category} fieldName="equipment_item" isEditing={sectionEditing("equipment")} canEdit={ce("equipment_item")} onChange={(_, v) => setEquipForms((prev) => prev.map((item, idx) => idx === i ? { ...item, category: v } : item))} icon={ClipboardList} />
                <InlineEditableField label={t("profile.equipmentFields.assignedDate")} value={eq.assignedDate} fieldName="equipment_status" type="date" isEditing={sectionEditing("equipment")} canEdit={ce("equipment_status")} onChange={(_, v) => setEquipForms((prev) => prev.map((item, idx) => idx === i ? { ...item, assignedDate: v } : item))} icon={Calendar} />
                <InlineEditableField label={t("profile.equipmentFields.returnDate")} value={eq.returnDate} fieldName="equipment_status" type="date" isEditing={sectionEditing("equipment")} canEdit={ce("equipment_status")} onChange={(_, v) => setEquipForms((prev) => prev.map((item, idx) => idx === i ? { ...item, returnDate: v } : item))} icon={Calendar} />
                <InlineEditableField label={t("profile.equipmentFields.expiryDate")} value={eq.expiryDate} fieldName="equipment_status" type="date" isEditing={sectionEditing("equipment")} canEdit={ce("equipment_status")} onChange={(_, v) => setEquipForms((prev) => prev.map((item, idx) => idx === i ? { ...item, expiryDate: v } : item))} icon={Calendar} />
                <InlineEditableField label={t("profile.equipmentFields.status")} value={eq.status} fieldName="equipment_status" type="select" options={[{ value: "Active", label: t("equipmentManagement.status.Active") }, { value: "Expired", label: t("equipmentManagement.status.Expired") }, { value: "Returned", label: t("equipmentManagement.status.Returned") }, { value: "Lost", label: t("equipmentManagement.status.Lost") }]} isEditing={sectionEditing("equipment")} canEdit={ce("equipment_status")} onChange={(_, v) => setEquipForms((prev) => prev.map((item, idx) => idx === i ? { ...item, status: v as EquipmentStatus } : item))} icon={FileText} />
              </div>
            ))}
            {sectionEditing("equipment") && ce("equipment_item") && (
              <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={() => setEquipForms((prev) => [...prev, { itemName: "", serialNumber: "", externalId: "", category: "", assignedDate: "", returnDate: "", expiryDate: "", status: "Active" }])}>
                <Plus className="h-3.5 w-3.5 mr-1" /> {t("common.add")}
              </Button>
            )}
          </Section>
        </div>

        {/* Employment Details */}
        {showSection("employment_details") && (
          <div ref={(el) => { sectionRefs.current.employment_details = el; }}>
            <Section
              title={t("profile.employmentDetails")}
              icon={ClipboardList}
              sectionKey="employment_details"
              canEditAny={canEditAnyInSection("employment_details", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
              isEditing={sectionEditing("employment_details")}
              onEdit={() => setEditingSection("employment_details")}
              onSave={() => handleSave("employment_details")}
              onCancel={() => handleCancel("employment_details")}
              isSaving={empDetailsMutation.isPending}
            >
              <ReportsToEditor employee={employee} employees={allEmployees} />
              {/* Expiry warning banner */}
              {(() => {
                const d1 = daysUntil(empDetailsForm.contractEndDate);
                const d2 = daysUntil(empDetailsForm.probationEndDate);
                const urgent = (d1 !== null && d1 <= 30) ? { label: t("profile.contract"), date: empDetailsForm.contractEndDate, days: d1 } :
                               (d2 !== null && d2 <= 30) ? { label: t("profile.probation"), date: empDetailsForm.probationEndDate, days: d2 } : null;
                if (!urgent) return null;
                const isRed = urgent.days <= 7;
                return (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm mb-2 ${isRed ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>{t(urgent.days <= 0 ? "profile.endsToday" : "profile.endsInDays", { label: urgent.label, count: urgent.days })} — {urgent.date}</span>
                  </div>
                );
              })()}

              {/* Shared fields — all contract types */}
              {(selectedIsFixedTermContract || selectedContractType === "Contract" || selectedIsLegacyIntern) && (
                <InlineEditableField
                  label={selectedIsLegacyIntern ? t("profile.internshipEndDate") : t("profile.contractEndDate")}
                  value={empDetailsForm.contractEndDate || ""}
                  fieldName="contract_end_date"
                  type="date"
                  isEditing={sectionEditing("employment_details")}
                  canEdit={ce("contract_end_date")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, contractEndDate: v || null }))}
                  icon={Calendar}
                />
              )}
              {empDetailsErrors.contractEndDate && <p className="text-xs text-destructive px-1">{empDetailsErrors.contractEndDate}</p>}

              <InlineEditableField label={t("profile.workArrangement")} value={empDetailsForm.workArrangement || ""} fieldName="work_arrangement"
                type="select" options={[{ value: "on-site", label: t("profile.onSite") }, { value: "hybrid", label: t("profile.hybrid") }, { value: "remote", label: t("profile.remote") }]}
                isEditing={sectionEditing("employment_details")} canEdit={ce("work_arrangement")}
                onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, workArrangement: v as any || null }))} icon={MapPin} />

              <InlineEditableField label={t("profile.careerLevel")} value={empDetailsForm.careerLevel || ""} fieldName="career_level"
                isEditing={sectionEditing("employment_details")} canEdit={ce("career_level")}
                onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, careerLevel: v || null }))} icon={Briefcase} />

              <InlineEditableField label={t("profile.costCenter")} value={empDetailsForm.costCenter || ""} fieldName="cost_center"
                isEditing={sectionEditing("employment_details")} canEdit={ce("cost_center")}
                onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, costCenter: v || null }))} icon={Hash} />

              <InlineEditableField label={t("profile.benefitsEligible")} value={empDetailsForm.benefitsEligible ? "true" : "false"} fieldName="benefits_eligible"
                type="toggle" isEditing={sectionEditing("employment_details")} canEdit={ce("benefits_eligible")}
                onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, benefitsEligible: v === "true" }))} icon={ShieldCheck} />

              <InlineEditableField label={t("profile.noticePeriodDays")} value={empDetailsForm.noticePeriodDays != null ? String(empDetailsForm.noticePeriodDays) : ""} fieldName="notice_period_days"
                type="number" isEditing={sectionEditing("employment_details")} canEdit={ce("notice_period_days")}
                onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, noticePeriodDays: v ? Number(v) : null }))} icon={Clock} />

              {/* Full-time specific */}
              {selectedIsFullTimeContract && (<>
                <InlineEditableField label={t("profile.probationEndDate")} value={empDetailsForm.probationEndDate || ""} fieldName="probation_end_date"
                  type="date" isEditing={sectionEditing("employment_details")} canEdit={ce("probation_end_date")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, probationEndDate: v || null }))} icon={Calendar} />
                {empDetailsErrors.probationEndDate && <p className="text-xs text-destructive px-1">{empDetailsErrors.probationEndDate}</p>}
                <InlineEditableField label={t("profile.probationPeriodMonths")} value={empDetailsForm.probationPeriodMonths != null ? String(empDetailsForm.probationPeriodMonths) : ""} fieldName="probation_period_months"
                  type="number" isEditing={sectionEditing("employment_details")} canEdit={ce("probation_period_months")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, probationPeriodMonths: v ? Number(v) : null }))} icon={Clock} />
              </>)}

              {/* Part-time specific */}
              {selectedIsPartTimeContract && (<>
                <InlineEditableField
                  label={t("organization.addEmployeeEmployment.partTimeHoursPerDay")}
                  value={empDetailsForm.partTimeHoursPerDay != null ? String(empDetailsForm.partTimeHoursPerDay) : ""}
                  fieldName="part_time_hours_per_day"
                  type="number"
                  isEditing={sectionEditing("employment_details")}
                  canEdit={ce("part_time_hours_per_day")}
                  onChange={(_, v) => setEmpDetailsForm((p) => {
                    const dailyHours = v ? Number(v) : null;
                    return {
                      ...p,
                      partTimeHoursPerDay: dailyHours,
                      hoursPerWeek: calculatePartTimeWeeklyHours(dailyHours, p.partTimeIncludesSaturday),
                    };
                  })}
                  icon={Clock}
                />
                {empDetailsErrors.partTimeHoursPerDay && <p className="text-xs text-destructive px-1">{empDetailsErrors.partTimeHoursPerDay}</p>}
                <InlineEditableField
                  label={t("organization.addEmployeeEmployment.partTimeIncludesSaturday")}
                  value={!!empDetailsForm.partTimeIncludesSaturday}
                  fieldName="part_time_includes_saturday"
                  type="toggle"
                  isEditing={sectionEditing("employment_details")}
                  canEdit={ce("part_time_includes_saturday")}
                  onChange={(_, v) => setEmpDetailsForm((p) => {
                    const includesSaturday = v === "true";
                    return {
                      ...p,
                      partTimeIncludesSaturday: includesSaturday,
                      hoursPerWeek: calculatePartTimeWeeklyHours(p.partTimeHoursPerDay, includesSaturday),
                    };
                  })}
                  icon={Calendar}
                />
                <InlineEditableField
                  label={t("organization.addEmployeeEmployment.hoursPerWeek")}
                  value={partTimeWeeklyHours != null ? String(partTimeWeeklyHours) : ""}
                  fieldName="hours_per_week"
                  type="number"
                  isEditing={false}
                  canEdit={false}
                  onChange={() => undefined}
                  icon={Clock}
                />
                <InlineEditableField label={t("profile.ftePercentage")} value={empDetailsForm.ftePercentage != null ? String(empDetailsForm.ftePercentage) : ""} fieldName="fte_percentage"
                  type="number" isEditing={sectionEditing("employment_details")} canEdit={ce("fte_percentage")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, ftePercentage: v ? Number(v) : null }))} icon={Users2} />
                {empDetailsErrors.ftePercentage && <p className="text-xs text-destructive px-1">{empDetailsErrors.ftePercentage}</p>}
                <InlineEditableField label={t("profile.overtimeEligible")} value={empDetailsForm.overtimeEligible ? "true" : "false"} fieldName="overtime_eligible"
                  type="toggle" isEditing={sectionEditing("employment_details")} canEdit={ce("overtime_eligible")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, overtimeEligible: v === "true" }))} icon={ShieldCheck} />
              </>)}
              {/* Contract specific */}
              {selectedIsContractorContract && (<>
                <InlineEditableField label={t("profile.contractorCompany")} value={empDetailsForm.contractorCompany || ""} fieldName="contractor_company"
                  isEditing={sectionEditing("employment_details")} canEdit={ce("contractor_company")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, contractorCompany: v || null }))} icon={Building2} />
                <InlineEditableField label={t("profile.scopeOfWork")} value={empDetailsForm.scopeOfWork || ""} fieldName="scope_of_work"
                  type="textarea" isEditing={sectionEditing("employment_details")} canEdit={ce("scope_of_work")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, scopeOfWork: v || null }))} icon={FileText} />
                <InlineEditableField label={t("profile.billingCycle")} value={empDetailsForm.billingCycle || ""} fieldName="billing_cycle"
                  isEditing={sectionEditing("employment_details")} canEdit={ce("billing_cycle")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, billingCycle: v || null }))} icon={Landmark} />
                <InlineEditableField label={t("profile.ndaSigned")} value={empDetailsForm.ndaSigned ? "true" : "false"} fieldName="nda_signed"
                  type="toggle" isEditing={sectionEditing("employment_details")} canEdit={ce("nda_signed")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, ndaSigned: v === "true" }))} icon={ShieldCheck} />
                <InlineEditableField label={t("profile.timeTrackingRequired")} value={empDetailsForm.timeTrackingRequired ? "true" : "false"} fieldName="time_tracking_required"
                  type="toggle" isEditing={sectionEditing("employment_details")} canEdit={ce("time_tracking_required")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, timeTrackingRequired: v === "true" }))} icon={Clock} />
              </>)}

              {/* Intern specific */}
              {selectedIsLegacyIntern && (<>
                <InlineEditableField label={t("profile.internshipType")} value={empDetailsForm.internshipType || ""} fieldName="internship_type"
                  type="select" options={[{ value: "paid", label: t("profile.paid") }, { value: "unpaid", label: t("profile.unpaid") }]}
                  isEditing={sectionEditing("employment_details")} canEdit={ce("internship_type")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, internshipType: v as any || null }))} icon={Briefcase} />
                <InlineEditableField label={t("profile.mentorSupervisor")} value={empDetailsForm.mentorId != null ? String(empDetailsForm.mentorId) : ""} fieldName="mentor_id"
                  type="select" options={allEmployees.filter((e) => e.status === "Active" && e.id !== employee.id).map((e) => ({ value: e.id, label: e.name }))}
                  isEditing={sectionEditing("employment_details")} canEdit={ce("mentor_id")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, mentorId: v ? Number(v) : null }))} icon={User} />
                {empDetailsErrors.mentorId && <p className="text-xs text-destructive px-1">{empDetailsErrors.mentorId}</p>}
                <InlineEditableField label={t("profile.schoolUniversity")} value={empDetailsForm.schoolUniversity || ""} fieldName="school_university"
                  isEditing={sectionEditing("employment_details")} canEdit={ce("school_university")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, schoolUniversity: v || null }))} icon={GraduationCap} />
                <InlineEditableField label={t("profile.academicProgram")} value={empDetailsForm.academicProgram || ""} fieldName="academic_program"
                  isEditing={sectionEditing("employment_details")} canEdit={ce("academic_program")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, academicProgram: v || null }))} icon={FileText} />
                <InlineEditableField label={t("profile.expectedGraduation")} value={empDetailsForm.expectedGraduationDate || ""} fieldName="expected_graduation_date"
                  type="date" isEditing={sectionEditing("employment_details")} canEdit={ce("expected_graduation_date")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, expectedGraduationDate: v || null }))} icon={Calendar} />
                <InlineEditableField label={t("profile.conversionEligible")} value={empDetailsForm.isConversionEligible ? "true" : "false"} fieldName="is_conversion_eligible"
                  type="toggle" isEditing={sectionEditing("employment_details")} canEdit={ce("is_conversion_eligible")}
                  onChange={(_, v) => setEmpDetailsForm((p) => ({ ...p, isConversionEligible: v === "true" }))} icon={ShieldCheck} />
              </>)}
            </Section>
          </div>
        )}

        {/* Visa */}
        {showSection("visa") && (
          <div ref={(el) => { sectionRefs.current.visa = el; }}>
            <Section
              title={t("profile.sections.visa")}
              icon={Stamp}
              sectionKey="visa"
              canEditAny={canEditAnyInSection("visa", viewerRole, isOwnProfile, isLineManagerOfEmployee)}
              isEditing={sectionEditing("visa")}
              onEdit={() => setEditingSection("visa")}
              onSave={() => handleSave("visa")}
              onCancel={() => { setEditingSection(null); if (visa) setVisaForm({ permitType: visa.permitType || "", permitNumber: visa.permitNumber || "", issuingCountry: visa.issuingCountry || "", issueDate: visa.issueDate || "", expiryDate: visa.expiryDate || "" }); }}
              isSaving={visaMutation.isPending}
            >
              <InlineEditableField label={t("profile.visa.permitType")} value={visaForm.permitType} fieldName="visa_permit_type" isEditing={sectionEditing("visa")} canEdit={ce("visa_permit_type")} onChange={(_, v) => setVisaForm((p) => ({ ...p, permitType: v }))} icon={Stamp} />
              <InlineEditableField label={t("profile.visa.permitNumber")} value={visaForm.permitNumber} fieldName="visa_permit_number" isEditing={sectionEditing("visa")} canEdit={ce("visa_permit_number")} onChange={(_, v) => setVisaForm((p) => ({ ...p, permitNumber: v }))} icon={Hash} />
              <InlineEditableField label={t("profile.visa.issuingCountry")} value={visaForm.issuingCountry} fieldName="visa_issuing_country" type="select" options={COUNTRIES} isEditing={sectionEditing("visa")} canEdit={ce("visa_issuing_country")} onChange={(_, v) => setVisaForm((p) => ({ ...p, issuingCountry: v }))} icon={Globe} />
              <InlineEditableField label={t("profile.visa.issueDate")} value={visaForm.issueDate} fieldName="visa_issue_date" type="date" isEditing={sectionEditing("visa")} canEdit={ce("visa_issue_date")} onChange={(_, v) => setVisaForm((p) => ({ ...p, issueDate: v }))} icon={Calendar} />
              <InlineEditableField label={t("profile.visa.expiryDate")} value={visaForm.expiryDate} fieldName="visa_expiry_date" type="date" isEditing={sectionEditing("visa")} canEdit={ce("visa_expiry_date")} onChange={(_, v) => setVisaForm((p) => ({ ...p, expiryDate: v }))} icon={Calendar} />
            </Section>
          </div>
        )}
      </div>

      {/* Reassign Line Manager Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reassign Direct Reports</DialogTitle>
            <DialogDescription>
              {employee.name} is currently the Line Manager for {directReports.length} employee(s).
              To revoke their Line Manager status, you must reassign these employees to a new manager.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm font-medium mb-2">Affected Employees:</p>
            <div className="bg-muted/50 rounded-md p-3 mb-4 max-h-32 overflow-y-auto">
              <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                {directReports.map((r) => (
                  <li key={r.id}>{r.name}</li>
                ))}
              </ul>
            </div>

            <p className="text-sm font-medium mb-2">New Line Manager:</p>
            <EmployeeCombobox
              employees={allEmployees.filter((e) => e.id !== employee.id && e.isLineManager && e.status === "Active")}
              value={newManagerId}
              onChange={setNewManagerId}
              placeholder="Select new manager..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignDialog(false)}>
              Cancel
            </Button>
            <Button
              disabled={!newManagerId || coreMutation.isPending}
              onClick={() => coreMutation.mutate()}
            >
              {coreMutation.isPending ? "Saving..." : "Confirm & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>

      {/* Floating Save / Cancel bar — single-edit mode, follows the scroll */}
      {singleEditMode && editingAll && (
        <div className="sticky bottom-3 z-30 flex justify-end pointer-events-none mt-4">
          <div className="pointer-events-auto flex items-center gap-2 rounded-xl border border-border bg-background/95 backdrop-blur px-3 py-2 shadow-lg">
            <Button variant="outline" size="sm" onClick={handleGlobalCancel} disabled={bulkSaveMutation.isPending}>
              <X className="h-3.5 w-3.5 mr-1" />{t("common.cancel")}
            </Button>
            <Button size="sm" onClick={handleGlobalSave} disabled={bulkSaveMutation.isPending}>
              {bulkSaveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              {bulkSaveMutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
